import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import Portada from '../componentes/Portada'
import { formatearBytes } from '../lib/almacen'
import { borrarDescargado, listarDescargados, type CapituloDescargado } from '../lib/offline'

/**
 * Lo que hay guardado en el propio telefono.
 *
 * Esta pantalla no toca el servidor: es la que sigue funcionando en el metro
 * o en un avion.
 */
export default function Descargas() {
  const clienteConsultas = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['descargasLocales'],
    queryFn: listarDescargados,
  })

  const borrar = useMutation({
    mutationFn: (ids: number[]) => Promise.all(ids.map(borrarDescargado)).then(() => undefined),
    onSuccess: () => clienteConsultas.invalidateQueries({ queryKey: ['descargasLocales'] }),
  })

  const series = useMemo(() => {
    const grupos = new Map<number, CapituloDescargado[]>()
    for (const capitulo of data ?? []) {
      const lista = grupos.get(capitulo.mangaId) ?? []
      lista.push(capitulo)
      grupos.set(capitulo.mangaId, lista)
    }
    return [...grupos.values()].sort((a, b) => a[0]!.mangaTitulo.localeCompare(b[0]!.mangaTitulo))
  }, [data])

  const total = (data ?? []).reduce((suma, c) => suma + c.bytes, 0)

  // Que series tienen los capitulos a la vista. Empiezan todas plegadas: lo
  // que se quiere ver de un vistazo es que series llevas encima, no la lista
  // entera de capitulos de todas a la vez.
  const [abiertas, setAbiertas] = useState<Set<number>>(new Set())

  function alternar(mangaId: number) {
    setAbiertas((previas) => {
      const copia = new Set(previas)
      if (copia.has(mangaId)) copia.delete(mangaId)
      else copia.add(mangaId)
      return copia
    })
  }

  return (
    <>
      <h1>Descargas</h1>
      <p className="subtitulo">
        {data?.length
          ? `${data.length} capitulos en el movil · ${formatearBytes(total)}`
          : 'Capitulos guardados en este dispositivo'}
      </p>

      <Link
        className="boton"
        to="/viaje"
        style={{ display: 'block', textAlign: 'center', marginBottom: 22 }}
      >
        Me voy sin internet
      </Link>

      {isLoading && <p className="cargando">Cargando…</p>}

      {data?.length === 0 && (
        <div className="aviso">
          <strong>Nada guardado todavia</strong>
          <p>
            Entra en cualquier serie y toca el <b>↓</b> de un capitulo, o usa «Guardar 5 siguientes
            en el movil». Lo que guardes aqui se lee sin conexion.
          </p>
        </div>
      )}

      {series.map((capitulos) => {
        const primero = capitulos[0]!
        const bytes = capitulos.reduce((suma, c) => suma + c.bytes, 0)
        const abierta = abiertas.has(primero.mangaId)

        return (
          <section key={primero.mangaId} className="serie-descargada">
            <div className="cabecera-serie">
              <button
                className="serie-toque"
                onClick={() => alternar(primero.mangaId)}
                aria-expanded={abierta}
                title={abierta ? 'Plegar los capitulos' : 'Ver los capitulos'}
              >
                <div style={{ width: 46, flexShrink: 0 }}>
                  <Portada
                    mangaId={primero.mangaId}
                    ruta={primero.portada}
                    titulo={primero.mangaTitulo}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <strong>{primero.mangaTitulo}</strong>
                  <span className="meta">
                    {capitulos.length} {capitulos.length === 1 ? 'capitulo' : 'capitulos'} ·{' '}
                    {formatearBytes(bytes)}
                  </span>
                </div>
                <span className={abierta ? 'flecha-serie abierta' : 'flecha-serie'}>▸</span>
              </button>
              <button
                className="icono-accion"
                title="Borrar todos los de esta serie"
                onClick={() => {
                  if (confirm(`Borrar los ${capitulos.length} capitulos de ${primero.mangaTitulo}?`))
                    borrar.mutate(capitulos.map((c) => c.id))
                }}
              >
                🗑
              </button>
            </div>

            <div className={abierta ? 'plegable abierto' : 'plegable'}>
              <div className="plegable-interior">
                <ul className="lista">
                  {capitulos.map((capitulo) => (
                    <li key={capitulo.id}>
                      <Link to={`/leer/${capitulo.id}`}>
                        <span style={{ flex: 1 }}>
                          {capitulo.nombre}
                          <span className="meta">
                            {capitulo.paginas.length} paginas · {formatearBytes(capitulo.bytes)}
                          </span>
                        </span>
                        <button
                          className="icono-accion"
                          title="Borrar del movil"
                          onClick={(e) => {
                            e.preventDefault()
                            borrar.mutate([capitulo.id])
                          }}
                        >
                          🗑
                        </button>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        )
      })}
    </>
  )
}
